import { useEffect, useMemo, useState } from 'react'
import { supabase } from '../supabase'
import YouTubeEmbed, { fmtClock, clipBoundsFor } from './YouTubeEmbed'
import PlanDayComments from './PlanDayComments'

// ── READING PLAN DAY SHEET (member) ───────────────────────────────────────
// Rebuilt per docs/designs/PlanDay.dc.html / PlanDayDone.dc.html
// (docs/FIX-PLANS.md §3). All 5 days are open from the start (no locking) —
// the pill strip lets a member jump freely between days, and "Mark done" is
// a toggle, not one-way, since there's no gating consequence to worry about.
//
// Props:
//   plan          — { id, title, summary, ... }
//   days          — this plan's reading_plan_days rows, sorted by day_number
//   follow        — { id, days_completed, last_opened_day } for this user
//   initialDay    — day_number to open on
//   sermon        — the source sermon (Watch clip + "Open full sermon" link)
//   userChurchId  — the VIEWING user's own home church (profile.church_id) —
//                   comments always read/write against this, per
//                   FIX-PLANS.md §4 ("a follower's comment goes to their own
//                   church's thread"), not necessarily plan.church_id
//   onClose()
//   onOpenSermon()         — close this sheet and open the sermon sheet for `sermon`
//   onFollowChange(follow) — called after days_completed/last_opened_day changes

const GOLD = 'var(--gold)'
const SERIF = "'Source Serif 4', Georgia, serif"
const SANS = "'Plus Jakarta Sans', system-ui, sans-serif"

export default function ReadingPlanDaySheet({ plan, days, follow, initialDay, sermon, userId, userChurchId, onClose, onOpenSermon, onFollowChange, stacked }) {
  const [currentDay, setCurrentDay] = useState(initialDay || 1)
  const [localFollow, setLocalFollow] = useState(follow || { days_completed: [], last_opened_day: initialDay || 1 })
  const [saving, setSaving] = useState(false)
  const [watchOpen, setWatchOpen] = useState(false)
  const [watchRef, setWatchRef] = useState(null) // sermon_refs row for the clip, or null
  const [commentsShown, setCommentsShown] = useState(false)
  const [moreOpen, setMoreOpen] = useState(false)
  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false)
  const [leaving, setLeaving] = useState(false)
  const [nudge, setNudge] = useState(false)

  const day = useMemo(() => days.find(d => d.day_number === currentDay) || null, [days, currentDay])
  const completedSet = new Set(localFollow?.days_completed || [])
  const isDone = completedSet.has(currentDay)
  const allDone = completedSet.size >= 5

  // Reset the inline player + comment-reveal state when switching days.
  useEffect(() => { setWatchOpen(false); setWatchRef(null); setCommentsShown(false) }, [currentDay])

  useEffect(() => {
    let cancelled = false
    if (!watchOpen || !day?.sermon_ref_id) return
    supabase.from('sermon_refs').select('*').eq('id', day.sermon_ref_id).maybeSingle()
      .then(({ data }) => { if (!cancelled) setWatchRef(data || null) })
    return () => { cancelled = true }
  }, [watchOpen, day?.sermon_ref_id])

  // Fallback when there's no sermon_ref_id to look up — parse "&t=NNNs" out
  // of the stored watch_url so the clip still starts in the right place.
  const watchFallbackSeconds = useMemo(() => {
    const m = String(day?.watch_url || '').match(/[?&]t=(\d+)/)
    return m ? parseInt(m[1], 10) : 0
  }, [day?.watch_url])

  async function goToDay(n) {
    setCurrentDay(n)
    if (!localFollow?.id) return
    const next = { ...localFollow, last_opened_day: n }
    setLocalFollow(next)
    onFollowChange?.(next)
    await supabase.from('reading_plan_follows').update({ last_opened_day: n }).eq('id', localFollow.id)
  }

  async function toggleDone() {
    if (!localFollow?.id || saving) return
    setSaving(true)
    const has = completedSet.has(currentDay)
    const nextCompleted = has
      ? (localFollow.days_completed || []).filter(d => d !== currentDay)
      : [...(localFollow.days_completed || []), currentDay]
    const next = { ...localFollow, days_completed: nextCompleted, last_opened_day: currentDay }
    setLocalFollow(next)
    onFollowChange?.(next)
    await supabase.from('reading_plan_follows')
      .update({ days_completed: nextCompleted, last_opened_day: currentDay })
      .eq('id', localFollow.id)
    setSaving(false)
  }

  function shareDay() {
    const url = `https://thebiblecommunity.co.uk/plan/${plan.id}?day=${currentDay}`
    if (navigator.share) { navigator.share({ title: plan.title, url }).catch(() => {}) }
    else { try { navigator.clipboard.writeText(url) } catch (_e) {} }
  }

  // Leaving deletes the follow row outright (reading_plan_follows RLS
  // already allows deleting your own row — "Users manage own plan follows"
  // is `for all`). There's no "rejoin where you left off" — rejoining via
  // Plans starts a fresh follow, same as never having joined.
  async function leavePlan() {
    if (!localFollow?.id || leaving) return
    setLeaving(true)
    await supabase.from('reading_plan_follows').delete().eq('id', localFollow.id)
    setLeaving(false)
    setLeaveConfirmOpen(false)
    onFollowChange?.(null)
    onClose?.()
  }

  const bounds = watchRef ? clipBoundsFor(watchRef) : { start: watchFallbackSeconds, end: watchFallbackSeconds ? watchFallbackSeconds + 90 : null }

  // Stacked (opened from inside the sermon sheet, not from a page): tapping
  // outside never closes — nudge instead, mirroring FullSermonSheet §3d.
  function onBackdropClick(e) {
    if (e.target !== e.currentTarget) return
    if (!stacked) { onClose?.(); return }
    setNudge(false)
    requestAnimationFrame(() => setNudge(true))
  }

  return (
    <div style={S.backdrop} onClick={onBackdropClick}>
      <style>{'@keyframes rpds-nudge{0%{transform:translateY(0)}35%{transform:translateY(-14px)}70%{transform:translateY(3px)}100%{transform:translateY(0)}}'}</style>
      <div style={{ ...S.sheet, animation: nudge ? 'rpds-nudge .42s ease' : 'none' }} onAnimationEnd={() => setNudge(false)}>
        <div style={S.header}>
          <button onClick={onClose} aria-label="Back" style={S.iconBtn}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--ink)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M15 6l-6 6 6 6"/></svg>
          </button>
          <div style={S.headerTitle}>{plan.title}</div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <button onClick={shareDay} aria-label="Share" style={S.iconBtn}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--ink)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7M16 6l-4-4-4 4M12 2v14"/></svg>
            </button>
            {localFollow?.id && (
              <div style={{ position: 'relative' }}>
                <button onClick={() => setMoreOpen(o => !o)} aria-label="More" style={S.iconBtn}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--ink)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h.01M12 12h.01M19 12h.01"/></svg>
                </button>
                {moreOpen && (
                  <>
                    <div onClick={() => setMoreOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 630 }}/>
                    <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 631, background: 'var(--white)', borderRadius: 14, boxShadow: '0 8px 24px rgba(31,27,22,0.18)', overflow: 'hidden', minWidth: 150 }}>
                      <button type="button"
                        onClick={() => { setMoreOpen(false); setLeaveConfirmOpen(true) }}
                        style={{ display: 'block', width: '100%', textAlign: 'left', padding: '11px 16px', background: 'none', border: 'none', cursor: 'pointer', fontFamily: SANS, fontSize: 13.5, fontWeight: 600, color: '#C2412D' }}>
                        Leave this plan
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        <div style={S.pillsRow}>
          {[1, 2, 3, 4, 5].map(n => {
            const done = completedSet.has(n)
            const current = n === currentDay
            return (
              <button key={n} onClick={() => goToDay(n)} style={{
                ...S.dayPill,
                ...(done ? S.dayPillDone : current ? S.dayPillCurrent : S.dayPillFuture),
              }}>
                {done ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 7"/></svg> : n}
              </button>
            )
          })}
        </div>

        <div style={S.scroll}>
          {!day ? (
            <div style={{ color: 'var(--stone)', fontSize: 13 }}>This day isn't available.</div>
          ) : (
            <>
              <div style={S.dayLabel}>DAY {currentDay} OF 5</div>
              <h2 style={S.dayTitle}>{day.title || day.passage_ref}</h2>

              {isDone && (
                <div style={S.doneBar}>
                  <span style={S.doneBarIcon}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 7"/></svg>
                  </span>
                  <span style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 800 }}>Day {currentDay} done</div>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>Day {currentDay + 1 <= 5 ? currentDay + 1 : currentDay} opens tomorrow, or now if you like</div>
                  </span>
                  {currentDay < 5 && <span style={S.doneBarLink} onClick={() => goToDay(currentDay + 1)}>Day {currentDay + 1}</span>}
                </div>
              )}

              <div style={S.card}>
                <div style={S.cardLabel}>Read · {day.passage_ref}</div>
                <div style={{ fontFamily: SERIF, fontSize: 16, lineHeight: 1.6, color: 'var(--ink)', whiteSpace: 'pre-wrap' }}>{day.devotional}</div>
              </div>

              {(day.watch_url || day.sermon_ref_id) && (
                <div style={S.card}>
                  <div style={S.cardLabel}>Watch · {day.watch_label || '1 min from Sunday'}</div>
                  {!watchOpen ? (
                    <button onClick={() => setWatchOpen(true)} style={{ display: 'flex', gap: 12, alignItems: 'center', background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', width: '100%' }}>
                      <div style={{ position: 'relative', width: 104, height: 58, borderRadius: 10, overflow: 'hidden', background: 'var(--border)', flexShrink: 0 }}>
                        {sermon?.youtube_id && <img src={`https://i.ytimg.com/vi/${sermon.youtube_id}/mqdefault.jpg`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.currentTarget.style.display = 'none' }}/>}
                        <span style={{ position: 'absolute', left: '50%', top: '50%', width: 22, height: 22, marginLeft: -11, marginTop: -11, borderRadius: 11, background: 'rgba(255,255,255,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9 }}>▶</span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--ink)' }}>{day.watch_label ? `"${day.watch_label}"` : 'Watch this moment'}</div>
                        <div style={{ fontSize: 12, color: 'var(--stone)' }}>from {fmtClock(watchFallbackSeconds)}</div>
                      </div>
                    </button>
                  ) : sermon?.youtube_id ? (
                    <>
                      <div style={{ position: 'relative', width: '100%', aspectRatio: '16/9', borderRadius: 14, overflow: 'hidden', background: '#000' }}>
                        <YouTubeEmbed videoId={sermon.youtube_id} start={bounds.start} end={bounds.end}/>
                      </div>
                      {sermon && onOpenSermon && (
                        <button onClick={onOpenSermon} style={S.openSermonLink}>Open full sermon →</button>
                      )}
                    </>
                  ) : null}
                </div>
              )}

              <div style={S.card}>
                <div style={S.cardLabel}>Reflect</div>
                <div style={{ fontSize: 14, lineHeight: 1.55, color: '#3A332C', marginBottom: 10 }}>{day.reflection}</div>
              </div>

              <button onClick={toggleDone} disabled={saving} style={isDone ? S.doneBtn : S.markBtn}>
                {isDone ? `✓ Day ${currentDay} complete` : `Mark day ${currentDay} done`}
              </button>

              {allDone && (
                <div style={S.completeBanner}>You've completed this plan! 🎉</div>
              )}

              {/* Comments — before finishing, tucked away (anti-spoiler, not a
                  lock: Show reveals them anyway); after finishing, open by
                  default. docs/FIX-PLANS.md §3/§4. */}
              {userChurchId && (
                isDone || commentsShown ? (
                  <div style={S.card}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--ink)' }}>Comments</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--stone)' }}>Church only</span>
                    </div>
                    <PlanDayComments planDayId={day.id} churchId={userChurchId} userId={userId} placeholder={day.reflection}/>
                  </div>
                ) : (
                  <ShowCommentsRow planDayId={day.id} churchId={userChurchId} onShow={() => setCommentsShown(true)}/>
                )
              )}
            </>
          )}
        </div>
      </div>

      {leaveConfirmOpen && (
        <>
          <div onClick={() => { if (!leaving) setLeaveConfirmOpen(false) }}
            style={{ position: 'fixed', inset: 0, zIndex: 640, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(2px)' }}/>
          <div role="dialog" aria-modal="true" aria-labelledby="leave-plan-title"
            style={{ position: 'fixed', zIndex: 641, top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 'min(360px, calc(100vw - 32px))', background: 'var(--white)', border: '1px solid var(--border-strong)', borderRadius: 16, padding: '22px 22px 18px', fontFamily: SANS, boxShadow: '0 24px 60px rgba(0,0,0,0.25)' }}>
            <div id="leave-plan-title" style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink)', marginBottom: 10 }}>Leave this plan?</div>
            <div style={{ fontSize: 14, color: 'var(--stone)', lineHeight: 1.55, marginBottom: 18 }}>
              Your progress on {plan.title} will be lost. You can join it again later, but you'll start back at Day 1.
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" disabled={leaving} onClick={() => setLeaveConfirmOpen(false)}
                style={{ flex: 1, background: 'transparent', border: '1px solid var(--border-strong)', borderRadius: 10, padding: 11, fontSize: 14, fontWeight: 600, color: 'var(--ink)', cursor: leaving ? 'default' : 'pointer', opacity: leaving ? 0.5 : 1, fontFamily: SANS }}>
                Cancel
              </button>
              <button type="button" disabled={leaving} onClick={leavePlan}
                style={{ flex: 1, background: '#F97066', border: 'none', borderRadius: 10, padding: 11, fontSize: 14, fontWeight: 700, color: '#fff', cursor: leaving ? 'default' : 'pointer', opacity: leaving ? 0.7 : 1, fontFamily: SANS }}>
                {leaving ? 'Leaving…' : 'Leave plan'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

// Compact "n comments from [church] — Shown when you've finished today" row,
// with a count fetched quietly (so it's never a bare "Comments" placeholder).
function ShowCommentsRow({ planDayId, churchId, onShow }) {
  const [count, setCount] = useState(null)
  useEffect(() => {
    let cancelled = false
    supabase.from('reading_plan_day_comments').select('id', { count: 'exact', head: true })
      .eq('plan_day_id', planDayId).eq('church_id', churchId).eq('removed', false)
      .then(({ count: c }) => { if (!cancelled) setCount(c || 0) })
    return () => { cancelled = true }
  }, [planDayId, churchId])
  if (!count) return null
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'var(--white)', borderRadius: 18, padding: '12px 14px', marginTop: 14 }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--ink)' }}>{count} comment{count === 1 ? '' : 's'}</div>
        <div style={{ fontSize: 12, color: 'var(--stone)' }}>Shown when you've finished today</div>
      </div>
      <button onClick={onShow} style={{ background: 'none', border: 'none', color: 'var(--gold-ink)', fontSize: 12.5, fontWeight: 800, cursor: 'pointer', fontFamily: SANS }}>Show</button>
    </div>
  )
}

const S = {
  backdrop: { position: 'fixed', inset: 0, zIndex: 620, background: 'rgba(0,0,0,0.6)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', fontFamily: SANS },
  sheet: { background: 'var(--cream)', width: '100%', maxWidth: 480, margin: '0 auto', height: '100%', display: 'flex', flexDirection: 'column', borderLeft: '1px solid var(--border)', borderRight: '1px solid var(--border)' },
  header: { flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', gap: 10 },
  headerTitle: { flex: 1, minWidth: 0, textAlign: 'center', fontSize: 13, fontWeight: 800, color: 'var(--stone)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  iconBtn: { flexShrink: 0, width: 40, height: 40, borderRadius: 20, background: 'var(--white)', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },

  pillsRow: { flexShrink: 0, display: 'flex', gap: 6, padding: '0 18px 14px' },
  dayPill: { flex: 1, height: 30, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, cursor: 'pointer', border: 'none', fontFamily: SANS },
  dayPillDone: { background: '#7C6BC4', color: '#fff' },
  dayPillCurrent: { background: 'var(--white)', color: 'var(--ink)', boxShadow: '0 0 0 2px #7C6BC4' },
  dayPillFuture: { background: 'var(--white)', color: 'var(--stone)' },

  scroll: { flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '4px 18px calc(env(safe-area-inset-bottom) + 32px)' },
  dayLabel: { fontSize: 12.5, fontWeight: 800, color: 'var(--lavender-ink)', marginBottom: 2 },
  dayTitle: { fontSize: 24, fontWeight: 800, lineHeight: 1.2, color: 'var(--ink)', margin: '0 0 16px' },

  doneBar: { display: 'flex', alignItems: 'center', gap: 10, background: 'var(--lavender)', color: 'var(--lavender-ink)', borderRadius: 18, padding: '12px 14px', marginBottom: 16 },
  doneBarIcon: { flexShrink: 0, width: 28, height: 28, borderRadius: 14, background: '#7C6BC4', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  doneBarLink: { fontSize: 12.5, fontWeight: 800, textDecoration: 'underline', cursor: 'pointer', flexShrink: 0 },

  card: { display: 'flex', flexDirection: 'column', gap: 10, background: 'var(--white)', borderRadius: 22, padding: 16, marginBottom: 14 },
  cardLabel: { fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--lavender-ink)' },
  openSermonLink: { alignSelf: 'flex-start', background: 'none', border: '1.5px solid var(--gold)', borderRadius: 999, color: 'var(--gold-ink)', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: SANS, padding: '8px 14px' },

  markBtn: { width: '100%', textAlign: 'center', background: GOLD, border: 'none', borderRadius: 999, padding: 15, fontSize: 15, fontWeight: 800, color: 'var(--ink)', cursor: 'pointer', fontFamily: SANS, marginBottom: 14 },
  doneBtn: { width: '100%', textAlign: 'center', background: 'rgba(34,197,94,0.14)', border: '1px solid rgba(34,197,94,0.4)', color: '#1F7A52', borderRadius: 999, padding: 15, fontSize: 15, fontWeight: 800, cursor: 'pointer', fontFamily: SANS, marginBottom: 14 },
  completeBanner: { marginBottom: 14, textAlign: 'center', fontFamily: SERIF, fontSize: 15, color: 'var(--gold-ink)', background: 'rgba(232,200,122,0.08)', border: '1px solid #E8C87A44', borderRadius: 12, padding: 14 },
}
